import { apiUrls } from "../ApiUrls";
import { BaseApiService } from "../BaseApiService";
import { ApiResponse } from "../ApiResponse";

export interface Notification {
  id: number | string;
  recipientId?: number;
  recipientRole?: string;
  type: string;
  title: string;
  message: string;
  data?: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string | null;
}

interface NotificationPageResponse {
  notifications: Notification[];
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

class NotificationService extends BaseApiService {
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];

  private static toIsoString(value: unknown): string | undefined {
    if (typeof value === "string") {
      return value;
    }
    if (Array.isArray(value)) {
      const [
        year,
        month,
        day,
        hour = 0,
        minute = 0,
        second = 0,
        nano = 0,
      ] = value;
      const millis = Math.floor(Number(nano) / 1_000_000);
      const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        Number(millis)
      );
      return date.toISOString();
    }
    return undefined;
  }

  private static normalizeNotification(raw: any): Notification {
    const parseBoolean = (value: unknown): boolean | undefined => {
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value === 1;
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") return true;
        if (normalized === "false") return false;
        if (normalized === "1") return true;
        if (normalized === "0") return false;
      }
      return undefined;
    };

    const createdAt =
      NotificationService.toIsoString(raw?.createdAt) ??
      NotificationService.toIsoString(raw?.created_at) ??
      raw?.createdAt ??
      raw?.created_at ??
      "";
    const readAt =
      NotificationService.toIsoString(raw?.readAt) ??
      NotificationService.toIsoString(raw?.read_at) ??
      raw?.readAt ??
      raw?.read_at ??
      null;
    const isRead =
      parseBoolean(raw?.isRead) ??
      parseBoolean(raw?.is_read) ??
      false;

    return {
      id: raw?.id ?? raw?.notificationId ?? raw?.notification_id ?? "",
      recipientId: raw?.recipientId ?? raw?.recipient_id,
      recipientRole: raw?.recipientRole ?? raw?.recipient_role,
      type: raw?.type ?? "",
      title: raw?.title ?? "",
      message: raw?.message ?? "",
      data: raw?.data,
      isRead,
      createdAt,
      readAt,
    };
  }

  connect() {
    this.loadNotifications().catch((error) => {
      NotificationService.logError("Notifications: connect", error);
    });
  }

  disconnect() {
    // Placeholder for real-time teardown in future
  }

  subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.push(listener);
    listener([...this.notifications]);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notifyListeners() {
    const snapshot = [...this.notifications];
    this.listeners.forEach((listener) => listener(snapshot));
  }

  async loadNotifications(page = 1, limit = 20): Promise<Notification[]> {
    let response: ApiResponse<NotificationPageResponse>;
    try {
      response = await NotificationService.getAuthenticatedWithQuery(
        apiUrls.notifications.list,
        { page, limit },
        "Load notifications"
      );
    } catch (error) {
      NotificationService.logError("Notifications: loadNotifications", error);
      this.notifications = [];
      this.notifyListeners();
      return [];
    }

    const responseAny: any = response;
    console.log("Notifications API raw response:", responseAny);
    const candidatePayloads = [
      responseAny?.data,
      responseAny?.data?.data,
      responseAny?.data?.data?.data,
      responseAny,
    ];
    const payload = candidatePayloads.find((candidate) =>
      Array.isArray(candidate?.notifications)
    );
    console.log("Notifications payload resolved:", payload);

    if (
      (response.status === "success" && response.data) ||
      payload?.notifications
    ) {
      const rawNotifications = Array.isArray(payload?.notifications)
        ? payload.notifications
        : [];
      console.log("Notifications raw list:", rawNotifications);
      this.notifications = rawNotifications.map(NotificationService.normalizeNotification);
      console.log("Notifications normalized list:", this.notifications);
      this.notifyListeners();
      return this.notifications;
    }

    this.notifications = [];
    this.notifyListeners();
    return [];
  }

  async markAsRead(id: number | string): Promise<void> {
    try {
      await NotificationService.postAuthenticated(
        apiUrls.notifications.markAsRead(id),
        {},
        "Mark notification as read"
      );
    } catch (error) {
      NotificationService.logError("Notifications: markAsRead", error);
      return;
    }

    this.notifications = this.notifications.map((notification) =>
      notification.id === id
        ? { ...notification, isRead: true, readAt: new Date().toISOString() }
        : notification
    );
    this.notifyListeners();
  }

  async getUnreadCount(): Promise<number> {
    let response: ApiResponse<{ count: number }>;
    try {
      response = await NotificationService.getAuthenticated(
        apiUrls.notifications.unreadCount,
        "Get unread notifications count"
      );
    } catch (error) {
      NotificationService.logError("Notifications: getUnreadCount", error);
      return 0;
    }

    if (response.status === "success" && response.data) {
      return response.data.count;
    }
    return 0;
  }

  removeNotification(id: number | string) {
    this.notifications = this.notifications.filter((n) => n.id !== id);
    this.notifyListeners();
  }
}

const notificationService = new NotificationService();
export default notificationService;
